<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'
import { Modal } from 'bootstrap'
import { Preferences } from '@capacitor/preferences'

import { getVersion } from '../../services/versionApi'
import type { comparedVersionInterface } from '../../models/version'
import { useI18n } from 'vue-i18n'
import { Browser } from '@capacitor/browser'

const DISMISS_KEY = 'version_update_dismissed_until'
const { t } = useI18n()

const modalEl = ref<HTMLElement | null>(null)
let modalInstance: Modal | null = null

const versionInfo = ref<comparedVersionInterface | null>(null)

const skipDismissToken = ref(false)

export interface ComparedVersionInterface {
  needUpdate: boolean
  url: string
  version: string
}

async function checkVersion(parsedVersionResponse): Promise<ComparedVersionInterface> {
  if (!parsedVersionResponse.version || !parsedVersionResponse.version) {
    return Promise.reject(new Error(String(t('version-check-error'))))
  }
  const currentVersion = (import.meta.env.VITE_VERSION_NUMBER as string) || ''
  const remoteVersion = parsedVersionResponse.version
  const needUpdate = currentVersion ? currentVersion !== remoteVersion : false
  return {
    needUpdate,
    url: parsedVersionResponse.url,
    version: parsedVersionResponse.version,
  }
}

function nowMs(): number {
  return Date.now()
}

async function getDismissedUntil(): Promise<number> {
  const { value } = await Preferences.get({ key: DISMISS_KEY })
  const parsed = value ? Number(value) : 0
  return Number.isFinite(parsed) ? parsed : 0
}

async function setDismissedForOneHour(): Promise<void> {
  const dismissedUntil = nowMs() + 60 * 60 * 1000
  await Preferences.set({ key: DISMISS_KEY, value: String(dismissedUntil) })
}

async function onBootstrapHide() {
  if (skipDismissToken.value) return
  await setDismissedForOneHour()
}

onMounted(async () => {
  const dismissedUntil = await getDismissedUntil()
  if (nowMs() < dismissedUntil) return

  if (!modalEl.value) return

  modalInstance = new Modal(modalEl.value, {
    backdrop: true,
    keyboard: true,
  })

  modalEl.value.addEventListener('hide.bs.modal', onBootstrapHide)

  const parsedVersionResponse = await getVersion()
  const response = await checkVersion(parsedVersionResponse)
  versionInfo.value = response

  if (!response.needUpdate) return

  skipDismissToken.value = false
  modalInstance.show()
})

onBeforeUnmount(() => {
  if (!modalEl.value) return
  modalEl.value.removeEventListener('hide.bs.modal', onBootstrapHide)
  modalInstance?.dispose()
  modalInstance = null
})
async function onClickUpdate() {
  const url = versionInfo.value?.url
  if (!url) return
  skipDismissToken.value = true

  await openExternal(url)
  modalInstance?.hide()
}

async function openExternal(url: string): Promise<void> {
  if (!/^https?:\/\//i.test(url)) return
  await Browser.open({ url })
}
</script>

<template>
  <div ref="modalEl" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-16">{{ $t('new-version-header') }}</h4>
          <p class="mb-32">{{ $t('new-version-text') }}</p>
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('new-version-dismiss') }}
            </button>
            <button
              v-if="versionInfo?.url"
              type="button"
              class="btn btn-primary flex-1"
              @click="onClickUpdate"
            >
              {{ $t('new-version-update') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
